/**
 * Arena Wars Ads Manager
 * Coordinates WLE rendering loop and audio states with the Google H5 Games Ad Placement API.
 */

export const AdsState = {
    INIT: 'INIT',
    MENU: 'MENU',
    PREROLL: 'PREROLL',
    ACTIVE_GAME: 'ACTIVE_GAME',
    MIDROLL: 'MIDROLL',
    REWARD_AD: 'REWARD_AD'
};

let currentState = AdsState.INIT;
let wleEngine = null;
let adConfigInitialized = false;

/**
 * Register the Wonderland Engine instance with the Ads Manager.
 * @param {Object} engine - Wonderland Engine instance
 */
export function setEngine(engine) {
    wleEngine = engine;
    console.log('[AdsManager] WLE Engine successfully registered.');
    
    // Try to auto-initialize H5 ads config if API is present
    initH5Ads();
}

/**
 * Initialize Google H5 Games Ad Placement API configuration.
 */
export function initH5Ads() {
    if (typeof window.adConfig === 'function' && !adConfigInitialized) {
        try {
            window.adConfig({
                preloadAdBreaks: 'on',
                onReady: () => {
                    console.log('[AdsManager] Google H5 Games API is ready.');
                    adConfigInitialized = true;
                }
            });
        } catch (err) {
            console.error('[AdsManager] Error initializing adConfig:', err);
        }
    } else if (adConfigInitialized) {
        console.log('[AdsManager] H5 adConfig already initialized.');
    } else {
        console.log('[AdsManager] window.adConfig not found. Running in simulation mode.');
    }
}

/**
 * Pauses game rendering and mutes audio.
 */
export function pauseGame() {
    console.log('[AdsManager] Pausing gameplay loop and muting audio for ad break.');
    
    // Pause Wonderland Engine rendering
    if (wleEngine && wleEngine.scene) {
        wleEngine.scene.active = false;
        console.log('[AdsManager] WLE scene rendering disabled.');
    } else {
        console.warn('[AdsManager] Cannot pause engine: WLE instance not registered or scene not loaded.');
    }
    
    // Mute all Howler.js audio
    if (window.Howler && typeof window.Howler.mute === 'function') {
        window.Howler.mute(true);
        console.log('[AdsManager] Howler.js globally muted.');
    }
}

/**
 * Resumes game rendering and restores audio.
 */
export function resumeGame() {
    console.log('[AdsManager] Resuming gameplay loop and restoring audio after ad break.');
    
    // Resume Wonderland Engine rendering
    if (wleEngine && wleEngine.scene) {
        wleEngine.scene.active = true;
        console.log('[AdsManager] WLE scene rendering enabled.');
    }
    
    // Unmute Howler.js audio
    if (window.Howler && typeof window.Howler.mute === 'function') {
        window.Howler.mute(false);
        console.log('[AdsManager] Howler.js globally unmuted.');
    }
}

/**
 * Trigger H5 Games API Preroll (before entering session).
 * @param {Function} onComplete - Callback executed when preroll concludes
 */
export function triggerPreroll(onComplete) {
    console.log('[AdsManager] triggerPreroll invoked.');
    currentState = AdsState.PREROLL;
    
    if (typeof window.adBreak !== 'function') {
        console.log('[AdsManager] Simulation Mode: Starting 1.2s mock preroll.');
        pauseGame();
        setTimeout(() => {
            resumeGame();
            currentState = AdsState.ACTIVE_GAME;
            console.log('[AdsManager] Simulation Mode: Preroll completed.');
            if (onComplete) onComplete();
        }, 1200);
        return;
    }
    
    pauseGame();
    
    window.adBreak({
        type: 'preroll',
        name: 'game-preroll',
        beforeAd: () => {
            pauseGame();
        },
        afterAd: () => {
            // Safe cleanup if required
        },
        adBreakDone: (placementInfo) => {
            console.log('[AdsManager] Preroll done:', placementInfo);
            resumeGame();
            currentState = AdsState.ACTIVE_GAME;
            if (onComplete) onComplete();
        }
    });
}

/**
 * Trigger H5 Games API Midroll (between match transitions).
 * @param {Function} onComplete - Callback executed when midroll concludes
 */
export function triggerMidroll(onComplete) {
    console.log('[AdsManager] triggerMidroll invoked.');
    const oldState = currentState;
    currentState = AdsState.MIDROLL;
    
    if (typeof window.adBreak !== 'function') {
        console.log('[AdsManager] Simulation Mode: Starting 1.5s mock midroll.');
        pauseGame();
        setTimeout(() => {
            resumeGame();
            currentState = (oldState === AdsState.PREROLL) ? AdsState.ACTIVE_GAME : oldState;
            console.log('[AdsManager] Simulation Mode: Midroll completed.');
            if (onComplete) onComplete();
        }, 1500);
        return;
    }
    
    pauseGame();
    
    window.adBreak({
        type: 'next',
        name: 'match-end-midroll',
        beforeAd: () => {
            pauseGame();
        },
        afterAd: () => {
            // Safe cleanup
        },
        adBreakDone: (placementInfo) => {
            console.log('[AdsManager] Midroll done:', placementInfo);
            resumeGame();
            currentState = (oldState === AdsState.PREROLL) ? AdsState.ACTIVE_GAME : oldState;
            if (onComplete) onComplete();
        }
    });
}

/**
 * Trigger H5 Games API Rewarded Ad (for skin unlocks or shop rewards).
 * @param {Function} onSuccess - Callback when ad is fully viewed
 * @param {Function} onFailure - Callback when ad is skipped or unavailable
 */
export function triggerRewardAd(onSuccess, onFailure) {
    console.log('[AdsManager] triggerRewardAd invoked.');
    const oldState = currentState;
    currentState = AdsState.REWARD_AD;
    
    if (typeof window.adBreak !== 'function') {
        console.log('[AdsManager] Simulation Mode: Running 2.0s mock rewarded ad.');
        pauseGame();
        setTimeout(() => {
            resumeGame();
            currentState = oldState;
            
            // Mock Success logic
            console.log('[AdsManager] Simulation Mode: Rewarded ad finished viewing.');
            if (onSuccess) onSuccess();
        }, 2000);
        return;
    }
    
    let isRewarded = false;
    pauseGame();
    
    window.adBreak({
        type: 'reward',
        name: 'shop-reward-coins',
        beforeAd: () => {
            pauseGame();
        },
        afterAd: () => {
            // Handled via adDismissed or adViewed
        },
        beforeReward: (showAdFn) => {
            // The API specifies we must prompt the user or trigger showAdFn directly
            showAdFn();
        },
        adDismissed: () => {
            console.log('[AdsManager] Rewarded ad dismissed by user.');
            resumeGame();
            currentState = oldState;
            if (onFailure) onFailure('dismissed');
        },
        adViewed: () => {
            console.log('[AdsManager] Rewarded ad successfully completed.');
            isRewarded = true;
            resumeGame();
            currentState = oldState;
            if (onSuccess) onSuccess();
        },
        adBreakDone: (placementInfo) => {
            console.log('[AdsManager] Reward ad placement complete:', placementInfo);
            
            // Fallback check: if the break concluded because there was no ad inventory
            if (placementInfo && placementInfo.breakStatus === 'notReady') {
                console.warn('[AdsManager] No rewarded ad inventory ready.');
                resumeGame();
                currentState = oldState;
                if (onFailure) onFailure('no_ad_ready');
            } else if (!isRewarded) {
                // If it finished but adViewed/adDismissed wasn't invoked, safeguard states
                resumeGame();
                currentState = oldState;
            }
        }
    });
}

/**
 * Helper to get the current state of the ad manager.
 */
export function getCurrentState() {
    return currentState;
}

// Bind to window for non-module script access in index.html
const adsManager = {
    setEngine,
    initH5Ads,
    pauseGame,
    resumeGame,
    triggerPreroll,
    triggerMidroll,
    triggerRewardAd,
    getCurrentState,
    AdsState
};
window.adsManager = adsManager;

export default adsManager;
